import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  CircularProgress,
  Button,
  Paper,
  IconButton,
} from "@mui/material";
import { Print, ArrowBack, FilterAltOff, ExpandMore, ChevronRight, FileDownload } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FaqButton } from "../components/FaqButton";
import { FooterInternal } from "../components/Footer";
import MultiLineChart, { ChartSeries } from "../components/charts/MultiLineChart";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { cashScenarioReportService, CashScenarioResponse } from "../services/reportsService";
import { colors, typography, pageHeader } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import MonthSelect from "../components/Inputs/MonthSelect";

// Cenário projeta meses futuros: competências selecionáveis até +12 meses
const scenarioMaxMonth = (() => {
  const d = new Date();
  d.setMonth(d.getMonth() + 12);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
})();


// Cenário de Caixa — projeção preventiva: posição estimada hoje + entradas previstas
// (recebimentos de pacientes), gastos fixos (provisões + média) e variáveis (repasses,
// impostos), mês a mês, para antecipar meses positivos/negativos.

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatCurrencyCompact = (value: number): string =>
  Math.abs(value) >= 1000
    ? `${(value / 1000).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}k`
    : String(Math.round(value));

const currentMonth = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const monthAdd = (month: string, delta: number): string => {
  const [year, monthNumber] = month.split("-").map((v) => parseInt(v, 10));
  const date = new Date(year, monthNumber - 1 + delta, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

// padrão aprovado: horizonte de 6 meses (corrente + 5)
const defaultToMonth = (): string => monthAdd(currentMonth(), 5);


const filterCardSx = {
  backgroundColor: "white",
  borderRadius: "12px",
  padding: "1.5rem",
  boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
  border: "1px solid #e9ecef",
  mb: 3,
};

const actionButtonSx = {
  padding: "0.5rem 1rem",
  borderRadius: "4px",
  fontSize: "0.85rem",
  fontWeight: typography.fontWeight.semibold,
  textTransform: "none" as const,
  boxShadow: "none",
  backgroundColor: colors.primary,
  color: "#fff",
  "&:hover": { backgroundColor: "#029AAB", boxShadow: "none" },
  "&.Mui-disabled": { backgroundColor: "#e9ecef", color: "#adb5bd" },
};

const actionButtonGraySx = {
  ...actionButtonSx,
  backgroundColor: "#6c757d",
  "&:hover": { backgroundColor: "#5a6268", boxShadow: "none" },
};

const clearFilterButtonSx = {
  backgroundColor: "#6c757d",
  color: "white",
  width: "40px",
  height: "40px",
  borderRadius: "4px",
  transition: "all 0.2s ease",
  "&:hover": { backgroundColor: "#5a6268" },
  "&:disabled": { backgroundColor: "#e9ecef", color: "#adb5bd", opacity: 0.6 },
};

const sectionStyle = {
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  mb: 3,
};

const summaryCardSx = {
  flex: "1 1 220px",
  background: "#fff",
  borderRadius: 2,
  border: "1px solid #e9ecef",
  p: 2,
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  minHeight: 88,
};

const ReportCashScenario: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [fromMonth, setFromMonth] = useState<string>(currentMonth());
  const [toMonth, setToMonth] = useState<string>(defaultToMonth());
  const [data, setData] = useState<CashScenarioResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const hasFilterChanges = fromMonth !== currentMonth() || toMonth !== defaultToMonth();

  const loadData = useCallback(async (from: string, to: string) => {
    if (!from || !to) return;
    setLoading(true);
    setError("");
    try {
      const response = await cashScenarioReportService.get(from, to);
      setData(response);
    } catch {
      setError("Não foi possível carregar o cenário de caixa. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleFromChange = (value: string) => {
    setFromMonth(value);
    if (value && toMonth) loadData(value, toMonth);
  };

  const handleToChange = (value: string) => {
    setToMonth(value);
    if (fromMonth && value) loadData(fromMonth, value);
  };

  const clearFilters = () => {
    const from = currentMonth();
    const to = defaultToMonth();
    setFromMonth(from);
    setToMonth(to);
    loadData(from, to);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await cashScenarioReportService.exportXlsx(fromMonth, toMonth);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `cenario_caixa_${fromMonth}_a_${toMonth}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      setError("Não foi possível exportar a planilha. Tente novamente.");
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    setTimeout(() => window.print(), 100);
  };

  useEffect(() => {
    loadData(currentMonth(), defaultToMonth());
  }, [loadData]);

  const toggleExpanded = (key: string) =>
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const projectedMonths = data ? data.months.filter((m) => m.endingBalance != null) : [];
  const lowestBalance = projectedMonths.length
    ? projectedMonths.reduce((min, m) =>
        (m.endingBalance as number) < (min.endingBalance as number) ? m : min
      )
    : null;

  const balanceSeries: ChartSeries[] = data
    ? [
        {
          name: "Saldo projetado",
          color: "#03B4C6",
          values: data.months.map((m) => m.endingBalance ?? null) as (number | null)[],
        },
        {
          name: "Entradas",
          color: "#28a745",
          values: data.months.map((m) => m.expectedInflow),
        },
        {
          name: "Saídas (fixos + repasses + impostos)",
          color: "#dc3545",
          values: data.months.map((m) => m.fixedExpenses + m.payouts + m.taxes),
        },
      ]
    : [];

  const headerCellSx: React.CSSProperties = {
    fontSize: 12,
    fontWeight: 600,
    color: "#6c757d",
    textTransform: "uppercase",
    padding: "0.5rem 0.75rem",
    whiteSpace: "nowrap",
    textAlign: "right",
  };

  const numberCellSx: React.CSSProperties = {
    fontSize: 14,
    padding: "0.5rem 0.75rem",
    textAlign: "right",
    whiteSpace: "nowrap",
    fontVariantNumeric: "tabular-nums",
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <style>{`
        @media print {
          .login-header, .login-footer-component, .no-print { display: none !important; }
          body { background: #fff !important; }
          main { margin-top: 0 !important; padding: 0 !important; background: #fff !important; }
          * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          /* tabelas largas: paisagem e sem scroll horizontal, para nada sair cortado */
          @page { size: A4 landscape; margin: 8mm; }
          [style*="overflow-x"], [style*="overflowX"] { overflow: visible !important; }
          table { min-width: 0 !important; width: 100% !important; font-size: 10px !important; }
          thead { display: table-header-group; }
          tr { break-inside: avoid; }
        }
      `}</style>

      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: 2,
            }}
          >
            <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
              <Typography variant="h4" className="c4u-page-header__title">
                Financeiro / Projeção de Caixa
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Projeção preventiva: posição estimada hoje + entradas previstas (recebimentos de
                pacientes), gastos fixos (provisões + média) e variáveis (repasses e impostos),
                mês a mês.
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          {/* Filtros e ações */}
          <Paper className="no-print" elevation={0} sx={filterCardSx}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: "0.75rem",
                alignItems: { xs: "stretch", sm: "flex-start" },
                mb: 2,
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: "0.75rem",
                  flex: 1,
                  maxWidth: 460,
                }}
              >
                <MonthSelect
                  label="Mês inicial"
                  value={fromMonth}
                  onChange={handleFromChange}
                  maxMonth={scenarioMaxMonth}
                  sx={filterFieldSx}
                />
                <MonthSelect
                  label="Mês final"
                  value={toMonth}
                  onChange={handleToChange}
                  maxMonth={scenarioMaxMonth}
                  sx={filterFieldSx}
                />
              </Box>
              <IconButton
                onClick={clearFilters}
                disabled={!hasFilterChanges || loading}
                title="Limpar filtros"
                sx={{ ...clearFilterButtonSx, flexShrink: 0, alignSelf: { xs: "flex-end", sm: "auto" } }}
              >
                <FilterAltOff fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: { sm: "flex-end" },
                flexWrap: { sm: "wrap" },
                gap: "0.5rem",
                // no celular cada ação ocupa a largura toda (alvo de toque confortável)
                "& .MuiButton-root": {
                  flexShrink: 0,
                  minHeight: 44,
                },
              }}
            >
              <Button
                variant="contained"
                startIcon={<ArrowBack />}
                onClick={() => navigateTo("report-cashflow")}
                sx={actionButtonGraySx}
              >
                Fluxo de Caixa
              </Button>
              <Button
                variant="contained"
                onClick={() => navigateTo("report-dre")}
                sx={actionButtonGraySx}
              >
                Demonstração do Resultado
              </Button>
              <Button
                variant="contained"
                startIcon={<FileDownload />}
                onClick={handleExport}
                disabled={exporting || loading}
                sx={actionButtonGraySx}
              >
                {exporting ? "Exportando..." : "Exportar planilha"}
              </Button>
              <Button
                variant="contained"
                startIcon={<Print />}
                onClick={handlePrint}
                disabled={loading || !data}
                sx={actionButtonSx}
              >
                Imprimir
              </Button>
            </Box>
          </Paper>

          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          )}

          {!loading && error && (
            <Box sx={{ ...sectionStyle, textAlign: "center", color: "#dc3545" }}>{error}</Box>
          )}

          {!loading && !error && data && (
            <>
              {/* Cards de posição */}
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 3 }}>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                    Saldo inicial estimado (contas correntes)
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: 22,
                      fontWeight: "bold",
                      color: data.startingBalance >= 0 ? "#28a745" : "#dc3545",
                    }}
                  >
                    {formatCurrency(data.startingBalance)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>
                    {data.accounts
                      .map((a) => `${a.name}: ${formatCurrencyCompact(a.balance)} (${a.balanceDate})`)
                      .join(" · ")}
                  </Typography>
                </Box>
                <Box sx={summaryCardSx}>
                  <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                    Reserva disponível (aplicações)
                  </Typography>
                  <Typography sx={{ fontSize: 22, fontWeight: "bold", color: "#03B4C6" }}>
                    {formatCurrency(data.reserveBalance)}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>
                    {data.reserveAccounts
                      .map((a) => `${a.name}: ${formatCurrencyCompact(a.balance)}`)
                      .join(" · ")}
                  </Typography>
                </Box>
                {lowestBalance && (
                  <Box sx={summaryCardSx}>
                    <Typography sx={{ fontSize: 13, color: "#6c757d" }}>
                      Menor saldo projetado no horizonte
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 22,
                        fontWeight: "bold",
                        color: (lowestBalance.endingBalance as number) >= 0 ? "#28a745" : "#dc3545",
                      }}
                    >
                      {formatCurrency(lowestBalance.endingBalance as number)}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: "#adb5bd" }}>
                      em {lowestBalance.month}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Tabela mês a mês */}
              <Box sx={{ ...sectionStyle, p: 0, overflowX: "auto" }}>
                <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e9ecef" }}>
                  <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold }}>
                    Cenário mês a mês
                  </Typography>
                </Box>
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 760 }}>
                  <thead>
                    <tr style={{ borderBottom: "1px solid #e9ecef" }}>
                      <th style={{ ...headerCellSx, textAlign: "left" }}>Mês</th>
                      <th style={headerCellSx}>Entradas previstas</th>
                      <th style={headerCellSx}>Gastos fixos</th>
                      <th style={headerCellSx}>Repasses</th>
                      <th style={headerCellSx}>Impostos</th>
                      <th style={headerCellSx}>Resultado</th>
                      <th style={headerCellSx}>Saldo projetado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.months.map((m) => {
                      const isOpen = !!expanded[m.month];
                      const expandable = m.fixedItems.length > 0;
                      return (
                        <React.Fragment key={m.month}>
                          <tr
                            onClick={expandable ? () => toggleExpanded(m.month) : undefined}
                            style={{
                              borderBottom: "1px solid #f1f3f5",
                              cursor: expandable ? "pointer" : "default",
                              backgroundColor:
                                m.endingBalance != null && m.endingBalance < 0
                                  ? "rgba(220, 53, 69, 0.06)"
                                  : "transparent",
                            }}
                          >
                            <td
                              style={{
                                fontSize: 14,
                                padding: "0.5rem 0.75rem",
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {expandable &&
                                (isOpen ? (
                                  <ExpandMore sx={{ fontSize: 18, color: "#6c757d" }} />
                                ) : (
                                  <ChevronRight sx={{ fontSize: 18, color: "#6c757d" }} />
                                ))}
                              {m.month}
                              {!m.isProjected && (
                                <Typography component="span" sx={{ fontSize: 11, color: "#adb5bd", ml: 0.5 }}>
                                  (real)
                                </Typography>
                              )}
                            </td>
                            <td style={numberCellSx}>
                              {formatCurrency(m.expectedInflow)}
                              {m.isProjected && m.inflowLow !== m.inflowHigh && (
                                <Typography component="div" sx={{ fontSize: 11, color: "#adb5bd" }}>
                                  {formatCurrencyCompact(m.inflowLow)} – {formatCurrencyCompact(m.inflowHigh)}
                                </Typography>
                              )}
                            </td>
                            <td style={numberCellSx}>{formatCurrency(m.fixedExpenses)}</td>
                            <td style={numberCellSx}>{formatCurrency(m.payouts)}</td>
                            <td style={numberCellSx}>{formatCurrency(m.taxes)}</td>
                            <td
                              style={{
                                ...numberCellSx,
                                fontWeight: 600,
                                color: m.netResult >= 0 ? "#28a745" : "#dc3545",
                              }}
                            >
                              {formatCurrency(m.netResult)}
                            </td>
                            <td
                              style={{
                                ...numberCellSx,
                                fontWeight: 600,
                                color:
                                  m.endingBalance == null
                                    ? "#adb5bd"
                                    : m.endingBalance >= 0
                                    ? colors.textPrimary
                                    : "#dc3545",
                              }}
                            >
                              {m.endingBalance == null ? "—" : formatCurrency(m.endingBalance)}
                            </td>
                          </tr>
                          {expandable &&
                            isOpen &&
                            m.fixedItems.map((item) => (
                              <tr key={`${m.month}-${item.name}`} style={{ borderBottom: "1px solid #f8f9fa" }}>
                                <td
                                  colSpan={2}
                                  style={{
                                    fontSize: 13,
                                    padding: "0.35rem 0.75rem 0.35rem 2.5rem",
                                    color: "#6c757d",
                                  }}
                                >
                                  {item.name}
                                  <Typography component="span" sx={{ fontSize: 11, color: "#adb5bd", ml: 1 }}>
                                    ({item.source})
                                  </Typography>
                                </td>
                                <td style={{ ...numberCellSx, fontSize: 13, color: "#6c757d" }}>
                                  {formatCurrency(item.value)}
                                </td>
                                <td colSpan={4} />
                              </tr>
                            ))}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </Box>

              {/* Gráfico do saldo projetado */}
              <Box sx={sectionStyle}>
                <Typography sx={{ fontSize: 15, fontWeight: typography.fontWeight.semibold, mb: 2 }}>
                  Saldo projetado × entradas × saídas
                </Typography>
                <MultiLineChart
                  labels={data.months.map((m) => m.month)}
                  series={balanceSeries}
                  valueFormatter={formatCurrency}
                  showValues
                  labelFormatter={formatCurrencyCompact}
                />
              </Box>

              {/* Procedimentos operacionais para manter o cenário confiável */}
              <Box sx={{ ...sectionStyle, py: 1.5, borderLeft: "3px solid #03B4C6" }}>
                <Typography sx={{ fontSize: 14, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                  Procedimentos para manter as contas atualizadas
                </Typography>
                {[
                  "Início de cada mês: lançar a transferência de saldo (SALDO EM CONTA) de cada banco no livro — é a base do saldo projetado. Banco sem lançamento no mês é considerado zerado.",
                  "Contas futuras: manter as previsões de despesa e de receita lançadas com o vencimento correto — são elas que compõem os gastos fixos e as entradas previstas dos próximos meses.",
                  "Pagamento realizado: lançar o efetivado normalmente na conciliação bancária. A previsão correspondente é reconhecida automaticamente pelo valor (±30%) e sai do cálculo.",
                  "Conta atrasada: reagendar o vencimento da previsão no livro para a nova data prevista de pagamento. Previsões vencidas sem pagamento identificado aparecem como \"em atraso\" no detalhamento do mês.",
                  "Previsão de mês anterior não paga: mover o vencimento para o mês corrente — vencidas de meses anteriores saem do cálculo até serem reagendadas.",
                  "Mudou a tributação? Atualizar o regime de impostos e as taxas da entidade (cadastro de regime) — os impostos projetados usam essas taxas sobre o bruto recebido.",
                ].map((item) => (
                  <Typography key={item} sx={{ fontSize: 12, color: "#6c757d", mb: 0.5 }}>
                    • {item}
                  </Typography>
                ))}
              </Box>

              {/* Metodologia da projeção */}
              <Box sx={{ ...sectionStyle, py: 1.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: typography.fontWeight.semibold, mb: 1 }}>
                  Como a projeção é calculada
                </Typography>
                {[
                  "Saldo inicial: último saldo lançado de cada conta corrente no livro financeiro + movimentos registrados desde essa data. Contas de aplicação ficam separadas como reserva e não entram no saldo operacional.",
                  "Entradas previstas: recebimentos de pacientes projetados por modelo estatístico de sazonalidade sobre o histórico desde jan/2025 (a faixa abaixo do valor é a banda de ~80% de confiança) + receitas previstas lançadas no livro (ex.: alocação de salas). No mês corrente, o valor já recebido é o piso.",
                  "Gastos fixos: previsões de despesa lançadas no livro com vencimento no mês (aluguel, empréstimos etc.) + complemento pela média dos 3 últimos meses fechados para o que não está provisionado — sem dupla contagem.",
                  "Repasses: valor de Repasse Previsto da agenda da competência anterior (o padrão da clínica é pagar no mês seguinte). Competências ainda sem agenda fechada usam o mesmo modelo sazonal.",
                  "Impostos: percentual do regime de impostos cadastrado para a entidade (soma das taxas ativas sobre o bruto recebido); sem cadastro, usa o percentual médio dos 3 últimos meses fechados.",
                  "Cancelamentos: a taxa média histórica já está embutida nas séries de recebimentos e repasses. Se a taxa dos últimos 3 meses piorar mais de 2 pontos percentuais em relação à média de 12 meses, as projeções são reduzidas proporcionalmente e um alerta aparece abaixo; melhora não infla a projeção.",
                  "Meses passados incluídos no filtro mostram o realizado (marcados como \"real\") e não recebem projeção.",
                ].map((item) => (
                  <Typography key={item} sx={{ fontSize: 12, color: "#6c757d", mb: 0.5 }}>
                    • {item}
                  </Typography>
                ))}
              </Box>

              {data.warnings.length > 0 && (
                <Box sx={{ ...sectionStyle, py: 1.5 }}>
                  {data.warnings.map((warning) => (
                    <Typography key={warning} sx={{ fontSize: 12, color: "#6c757d" }}>
                      • {warning}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          )}
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

export default ReportCashScenario;
